import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();
const STORAGE_KEY = 'reviterAuth';
const CURRENT_STORE_SLUG_KEY = 'reviterCurrentStoreSlug';

// Mapa papel global (legado) → role novo do backend. Usado só pra aliases de retrocompatibilidade.
const LEGACY_TYPE_FROM_ROLE = {
  platform_admin: 'admin',
  store_owner: 'supplier',
  reseller: 'reviter',
  client: 'client'
};

function getMembershipStoreId(membership) {
  if (!membership) return null;
  return membership.storeId ?? membership.id ?? membership.Store?.id ?? null;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
};

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStorage(payload) {
  if (payload) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } else {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CURRENT_STORE_SLUG_KEY);
  }
}

function writeCurrentStoreSlug(slug) {
  if (slug) localStorage.setItem(CURRENT_STORE_SLUG_KEY, slug);
  else localStorage.removeItem(CURRENT_STORE_SLUG_KEY);
}

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => readStorage());
  const [loading, setLoading] = useState(false);

  // Sincroniza currentStoreSlug no localStorage com o currentStoreId do auth,
  // pra que api.js injete o header automaticamente nas próximas requests.
  useEffect(() => {
    if (!auth?.stores?.length || !auth.currentStoreId) {
      writeCurrentStoreSlug(null);
      return;
    }
    const cur = auth.stores.find((s) => getMembershipStoreId(s) === auth.currentStoreId);
    writeCurrentStoreSlug(cur?.slug || null);
  }, [auth]);

  const persist = useCallback((payload) => {
    writeStorage(payload);
    setAuth(payload);
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const data = await authService.login(email, password);
      const currentStoreId = getMembershipStoreId(data.stores?.[0]) ?? null;
      persist({
        user: data.user,
        stores: data.stores || [],
        token: data.token,
        refreshToken: data.refreshToken,
        currentStoreId
      });
      return { success: true, user: data.user, stores: data.stores };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [persist]);

  const signup = useCallback(async (userData) => {
    setLoading(true);
    try {
      const data = await authService.signup(userData);
      persist({
        user: data.user,
        stores: data.stores || [],
        token: data.token,
        refreshToken: data.refreshToken,
        currentStoreId: getMembershipStoreId(data.stores?.[0]) ?? null
      });
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [persist]);

  const logout = useCallback(async () => {
    try { await authService.logout(); } catch {}
    persist(null);
  }, [persist]);

  const setCurrentStore = useCallback((storeId) => {
    setAuth((prev) => {
      if (!prev) return prev;
      const normalizedStoreId = storeId == null ? null : Number(storeId);
      const exists = prev.stores?.some((s) => getMembershipStoreId(s) === normalizedStoreId);
      const next = { ...prev, currentStoreId: exists ? normalizedStoreId : null };
      writeStorage(next);
      return next;
    });
  }, []);

  // Re-sync profile a partir do backend (útil após join/leave de loja).
  const refreshProfile = useCallback(async () => {
    if (!auth?.token) return;
    try {
      const data = await authService.getProfile();
      const stores = data.stores || [];
      const stillValidCurrentStore = stores.some((s) => getMembershipStoreId(s) === auth.currentStoreId);
      persist({
        ...auth,
        user: data.user,
        stores,
        currentStoreId: stillValidCurrentStore ? auth.currentStoreId : (getMembershipStoreId(stores[0]) ?? null)
      });
    } catch (err) {
      if (err.status === 401) persist(null);
    }
  }, [auth, persist]);

  // Atualiza o perfil do user logado. `data` = { name, phone, email, preferredLanguage,
  // currentPassword?, newPassword? }.
  const updateUser = useCallback(async (data) => {
    try {
      const res = await authService.updateProfile(null, data);
      setAuth((prev) => {
        if (!prev) return prev;
        const next = { ...prev, user: res.user, stores: res.stores || prev.stores };
        writeStorage(next);
        return next;
      });
      return { success: true, user: res.user };
    } catch (err) {
      return { success: false, error: err.message, details: err.details };
    }
  }, []);

  // Tokens mudarem via refresh no api.js não são observados diretamente.
  // Escutamos storage events pra pegar mudanças em outra aba.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setAuth(readStorage());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const currentStore = useMemo(() => {
    if (!auth?.stores?.length || !auth.currentStoreId) return null;
    return auth.stores.find((s) => getMembershipStoreId(s) === auth.currentStoreId) || null;
  }, [auth]);

  const roleInCurrentStore = currentStore?.roleInStore || null;
  const userRole = auth?.user?.role || null;

  const value = useMemo(() => {
    const isAuthenticated = !!auth?.token;
    const isPlatformAdmin = userRole === 'platform_admin';
    const isStoreOwner = userRole === 'store_owner' ||
      (auth?.stores || []).some(s => s.roleInStore === 'owner');
    const isResellerInCurrentStore = roleInCurrentStore === 'reseller';
    const isClientInCurrentStore = roleInCurrentStore === 'client';

    // Aliases legados — derivados do novo shape.
    const legacyType = LEGACY_TYPE_FROM_ROLE[userRole] || null;
    const isReviter = userRole === 'reseller';
    const isSupplier = userRole === 'store_owner';
    const isClient = userRole === 'client';

    return {
      // Novo shape
      user: auth?.user || null,
      stores: auth?.stores || [],
      currentStore,
      currentStoreId: auth?.currentStoreId || null,
      roleInCurrentStore,
      token: auth?.token || null,

      // Flags novas
      isAuthenticated,
      isPlatformAdmin,
      isStoreOwner,
      isResellerInCurrentStore,
      isClientInCurrentStore,

      // Aliases legados (deprecated — usar roleInCurrentStore + store.businessType)
      isReviter,
      isSupplier,
      isClient,
      legacyType,

      // Ações
      loading,
      login,
      signup,
      logout,
      setCurrentStore,
      refreshProfile,

      updateUser
    };
  }, [auth, userRole, currentStore, roleInCurrentStore, loading, login, signup, logout, setCurrentStore, refreshProfile, updateUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
